import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { MapPin, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const EMPTY = { label: 'Home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '', landmark: '', is_default: false };

export default function AddressBook() {
    const { type, loading } = useAuth();
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const reload = () => api.get('/customer/addresses').then(({ data }) => setList(data)).catch(() => {});
    useEffect(() => { if (type === 'customer') reload(); }, [type]);

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/account/addresses" replace />;

    const save = async () => {
        if (!editing.line1 || !editing.city || !editing.pincode || !editing.phone) {
            toast.error('Please fill required fields');
            return;
        }
        setSaving(true);
        try {
            if (editing.id) {
                await api.patch(`/customer/addresses/${editing.id}`, editing);
                toast.success('Address updated');
            } else {
                await api.post('/customer/addresses', editing);
                toast.success('Address added');
            }
            setEditing(null);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save');
        } finally { setSaving(false); }
    };

    const remove = async (a) => {
        if (!window.confirm('Delete this address?')) return;
        await api.delete(`/customer/addresses/${a.id}`);
        toast.success('Address removed');
        reload();
    };

    const setDefault = async (a) => {
        await api.post(`/customer/addresses/${a.id}/default`);
        toast.success(`${a.label} set as default`);
        reload();
    };

    return (
        <div className="bg-cream min-h-screen" data-testid="addressbook-page">
            <section className="bg-inorbit-blue-deeper text-white pt-28 pb-10">
                <div className="max-w-5xl mx-auto px-6 md:px-12 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <Link to="/profile" className="text-xs uppercase tracking-widest text-inorbit-yellow hover:underline">← Account</Link>
                        <h1 className="font-serif text-4xl md:text-5xl mt-3" style={{ color: 'white' }}>Address book</h1>
                        <p className="text-white/75 text-sm mt-2">Save your delivery destinations for faster checkout.</p>
                    </div>
                    <button onClick={() => setEditing({ ...EMPTY })} className="btn-yellow" data-testid="add-address-btn">
                        <Plus size={14} /> Add address
                    </button>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-2 gap-5">
                {list.length === 0 && (
                    <div className="md:col-span-2 card-luxury p-12 text-center" data-testid="addresses-empty">
                        <MapPin size={42} className="text-inorbit-orange mx-auto mb-3" strokeWidth={1.2} />
                        <div className="font-serif text-2xl text-inorbit-blue mb-2">No saved addresses</div>
                        <p className="text-text-secondary text-sm mb-4">Add your first to skip filling it out at checkout.</p>
                        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary mx-auto">
                            <Plus size={14} /> Add address
                        </button>
                    </div>
                )}
                {list.map((a) => (
                    <div key={a.id} className="card-luxury p-6 relative" data-testid={`address-card-${a.id}`}>
                        {a.is_default && (
                            <div className="absolute top-3 right-3 pill pill-yellow text-[9px]">Default</div>
                        )}
                        <div className="font-serif text-xl text-inorbit-blue mb-1">{a.label}</div>
                        <div className="text-sm text-text-secondary leading-relaxed">
                            <div className="font-medium text-inorbit-blue">{a.name}</div>
                            <div>{a.line1}</div>
                            {a.line2 && <div>{a.line2}</div>}
                            <div>{a.city}, {a.state} {a.pincode}</div>
                            {a.landmark && <div className="text-xs text-text-tertiary mt-1">📍 {a.landmark}</div>}
                            <div className="text-xs text-text-tertiary mt-1">📞 {a.phone}</div>
                        </div>
                        <div className="mt-4 flex gap-2">
                            {!a.is_default && (
                                <button onClick={() => setDefault(a)} className="text-xs uppercase tracking-widest text-inorbit-orange font-semibold hover:underline">
                                    <Check size={11} className="inline mr-1" /> Make default
                                </button>
                            )}
                            <button onClick={() => setEditing(a)} className="text-xs uppercase tracking-widest text-inorbit-blue font-semibold hover:underline" data-testid={`edit-${a.id}`}>
                                <Edit size={11} className="inline mr-1" /> Edit
                            </button>
                            <button onClick={() => remove(a)} className="text-xs uppercase tracking-widest text-rose-600 font-semibold hover:underline" data-testid={`delete-${a.id}`}>
                                <Trash2 size={11} className="inline mr-1" /> Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* EDIT MODAL */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-inorbit-blue-deeper/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="address-modal">
                    <div className="bg-white max-w-lg w-full max-h-[92vh] overflow-y-auto rounded-lg p-7" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <div className="overline">{editing.id ? 'Edit' : 'Add'} address</div>
                                <h2 className="font-serif text-2xl text-inorbit-blue mt-1">Delivery details</h2>
                            </div>
                            <button onClick={() => setEditing(null)} aria-label="Close" data-testid="address-modal-close"><X size={22} /></button>
                        </div>
                        <div className="space-y-3">
                            <select value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="input-box w-full" data-testid="addr-label">
                                {['Home', 'Office', 'Parents', 'Friend', 'Other'].map((l) => <option key={l}>{l}</option>)}
                            </select>
                            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Full name" className="input-box w-full" data-testid="addr-name" />
                            <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Mobile" className="input-box w-full" data-testid="addr-phone" />
                            <input value={editing.line1} onChange={(e) => setEditing({ ...editing, line1: e.target.value })} placeholder="House / flat / building" className="input-box w-full" data-testid="addr-line1" />
                            <input value={editing.line2} onChange={(e) => setEditing({ ...editing, line2: e.target.value })} placeholder="Street / area (optional)" className="input-box w-full" data-testid="addr-line2" />
                            <div className="grid grid-cols-3 gap-2">
                                <input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} placeholder="City" className="input-box col-span-1" data-testid="addr-city" />
                                <input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} placeholder="State" className="input-box col-span-1" data-testid="addr-state" />
                                <input value={editing.pincode} onChange={(e) => setEditing({ ...editing, pincode: e.target.value })} placeholder="Pincode" className="input-box col-span-1" data-testid="addr-pincode" />
                            </div>
                            <input value={editing.landmark} onChange={(e) => setEditing({ ...editing, landmark: e.target.value })} placeholder="Landmark (optional)" className="input-box w-full" />
                            <label className="flex items-center gap-2 text-sm text-text-secondary mt-2">
                                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="addr-default" />
                                Set as default delivery address
                            </label>
                            <button onClick={save} disabled={saving} className="btn-primary w-full justify-center disabled:opacity-50 mt-2" data-testid="addr-save-btn">
                                {saving ? 'Saving…' : editing.id ? 'Save changes' : 'Save address'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
